/**
 * WizardViewModeRail — the ViewMode sub-navigation for the acquired Wizard V2 step 3
 * (Financial Data / Transactions) when it renders CHROMELESS inside the Data-Specialist
 * Case Workspace shell.
 *
 * WHY THIS EXISTS: step 3's ViewMode navigation (Upload · Files · Summary · Monthly ·
 * Transactions · Categories · Tax · Revenue Sources · Reconciliation · Anomalies) used to
 * live only in the wizard's left SIDEBAR. Chromeless mode suppresses that sidebar, which
 * orphaned every view except the default — the DS could no longer reach the parser-wired
 * Upload or the bank/CC/merchant drill-downs. This rail re-surfaces exactly those items
 * (the same `sections` the sidebar was fed) so `setActiveView` is reachable again. It adds
 * NO navigation of its own and drives no data — a pure controlled reflection of ViewMode.
 *
 * Design: K3 ruling 2026-09-06 (DS Transactions ViewMode rail). 224px column on the app
 * base, three mono-caps groups, neutral active (never orange — the stage underline +
 * Continue own the orange budget), counts in DM Mono, the Anomalies count the one red.
 *
 * THEMING (2026-09-06): every color is a design token (var(--color-*)), NOT hardcoded hex,
 * so the rail inverts correctly under the app's Daylight (data-theme="light") toggle. The
 * original K3 hex build was dark-only — near-white labels/active-rule went invisible on the
 * light ground. Token roles preserve the K3 hierarchy: caption=text-dim, inactive=text-muted,
 * active/hover=text-primary, active fill=bg-hover, active rule=text-primary (neutral marker),
 * anomalies count=blocker. See index.css :root vs :root[data-theme="light"].
 */
import React from 'react';
import type { SidebarSection } from './WizardSidebar';

// K3 group map — which ViewMode id lands in which rail group, and the order within it.
// Tax Docs moves up into SOURCES (it is a parsed document class, not a derived check).
// Revenue Sources sits group-last in SOURCES so its conditional appear/disappear never
// shifts the memorized positions of LEDGER/CHECKS. `review` is the locked upload-mode row.
const GROUPS: { title: string; ids: string[] }[] = [
  { title: 'SOURCES', ids: ['upload', 'files', 'tax', 'revenue_sources'] },
  { title: 'LEDGER', ids: ['summary', 'monthly', 'transactions', 'categories', 'review'] },
  { title: 'CHECKS', ids: ['reconciliation', 'anomalies'] },
];

// Labels in `sections` embed their count as "Label (N)" (e.g. "Transactions (2,167)").
// Split it so the count can render right-aligned in DM Mono, per K3.
function splitLabel(label: string): { text: string; count: string | null } {
  const m = label.match(/^(.*?)\s*\((\d[\d,]*)\)\s*$/);
  if (m) return { text: m[1].trim(), count: m[2] };
  return { text: label, count: null };
}

export interface WizardViewModeRailProps {
  sections: SidebarSection[];
}

export default function WizardViewModeRail({ sections }: WizardViewModeRailProps) {
  const byId = new Map(sections.map((s) => [s.id, s]));

  return (
    // Outer cell fills the 224px grid column and STRETCHES to the content row height
    // (the grid must NOT set align-items:start, or this collapses and both the sticky
    // travel and the full-height hairline die — K3 rail review 2026-09-06). The hairline
    // lives here so it runs the full stage height; the nav inside sticks as you scroll.
    <div style={{ borderRight: '1px solid var(--color-border)', minHeight: '100%' }}>
      <nav
        aria-label="Financial data views"
        data-testid="ds-viewmode-rail"
        style={{
          position: 'sticky',
          top: 12,
          padding: '4px 20px 24px 0',
        }}
      >
      {GROUPS.map((group, gi) => {
        const items = group.ids.map((id) => byId.get(id)).filter(Boolean) as SidebarSection[];
        if (items.length === 0) return null; // e.g. CHECKS omitted in upload mode
        return (
          <div key={group.title}>
            <div
              className="font-mono"
              style={{
                fontSize: 10,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '.14em',
                color: 'var(--color-text-dim)',
                paddingLeft: 12,
                margin: gi === 0 ? '4px 0 4px' : '22px 0 4px',
                userSelect: 'none',
              }}
            >
              {group.title}
            </div>
            {items.map((s) => (
              <RailItem key={s.id} section={s} />
            ))}
          </div>
        );
      })}
      </nav>
    </div>
  );
}

function RailItem({ section }: { section: SidebarSection }) {
  const { text, count } = splitLabel(section.label);
  const active = !!section.isActive;
  const locked = !!(section as any).isLocked;
  const isAnomalies = section.id === 'anomalies';
  const isZero = count != null && count.replace(/,/g, '') === '0';
  // Anomalies is the one red in the rail: count is red only when > 0.
  const anomalyRed = isAnomalies && count != null && !isZero;
  // Suppress a "0" count everywhere except Anomalies, where 0 ("no anomalies") is information (K3 nit).
  const showCount = count != null && (isAnomalies || !isZero);

  const [hover, setHover] = React.useState(false);
  const showActive = active && !locked;

  // All tokens (var(--color-*)) so the rail inverts under Daylight (data-theme="light").
  // Hierarchy: locked=text-dim (dormant), inactive=text-muted, active/hover=text-primary.
  const labelColor = locked
    ? 'var(--color-text-dim)'
    : showActive || hover
      ? 'var(--color-text-primary)'
      : 'var(--color-text-secondary)'; // K3 2026-09-06: inactive was text-muted → read as disabled; raise to secondary
  const countColor = anomalyRed
    ? 'var(--color-blocker)'
    : showActive
      ? 'var(--color-text-muted)'
      : 'var(--color-text-dim)';
  const fill = locked
    ? 'transparent'
    : showActive
      ? 'var(--color-bg-hover)'
      : hover
        ? 'var(--color-bg-elevated)'
        : 'transparent';

  return (
    <>
      <button
        type="button"
        onClick={locked ? undefined : section.onClick}
        disabled={locked}
        aria-current={showActive ? 'true' : undefined}
        aria-disabled={locked || undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-viewmode={section.id}
        data-active={showActive ? 'true' : undefined}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: 32,
          padding: '0 12px',
          margin: '1px 0',
          borderRadius: 6,
          border: 'none',
          background: fill,
          cursor: locked ? 'not-allowed' : 'pointer',
          transition: 'background 120ms ease-out',
          textAlign: 'left',
        }}
      >
        {showActive && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 2,
              borderRadius: 2,
              background: 'var(--color-text-primary)',
            }}
          />
        )}
        <span
          className="font-sans"
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: labelColor,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {text}
        </span>
        <span
          className="font-mono"
          style={{
            marginLeft: 'auto',
            paddingLeft: 8,
            fontSize: 11,
            fontFeatureSettings: '"tnum"',
            color: countColor,
            flexShrink: 0,
          }}
        >
          {locked ? '🔒' : showCount ? count : null}
        </span>
      </button>
      {locked && (
        <div
          className="font-mono"
          style={{
            fontSize: 10,
            color: 'var(--color-text-dim)',
            padding: '2px 12px 0',
          }}
        >
          Unlocks after first parse
        </div>
      )}
    </>
  );
}
